import { useState } from "react";
import { usePriceAlerts } from "@/hooks/usePriceAlerts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Bell, Trash2, Loader2, MapPin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const Alerts = () => {
  const { alerts, loading, createAlert, toggleAlert, deleteAlert, isAuthenticated } = usePriceAlerts();
  const [label, setLabel] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [radius, setRadius] = useState("5");
  const [maxPrice, setMaxPrice] = useState("");
  const { toast } = useToast();

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      setLatitude(pos.coords.latitude.toFixed(6));
      setLongitude(pos.coords.longitude.toFixed(6));
    }, () => toast({ title: "Could not get location", variant: "destructive" }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const lat = Number(latitude), lng = Number(longitude), r = Number(radius), p = Number(maxPrice);
    if ([lat, lng, r, p].some((v) => Number.isNaN(v))) {
      toast({ title: "Invalid input", variant: "destructive" }); return;
    }
    await createAlert({ label: label || null, latitude: lat, longitude: lng, radius_km: r, max_price: p });
    setLabel(""); setMaxPrice("");
  };

  if (!isAuthenticated && !loading) {
    return <div className="container mx-auto px-4 py-12 text-center"><p>Please sign in to manage price alerts.</p></div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <h1 className="text-2xl font-bold mb-6 flex items-center"><Bell className="h-6 w-6 mr-2" /> Price Alerts</h1>

      <Card className="mb-8">
        <CardHeader><CardTitle>Create an alert</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="label">Label (optional)</Label>
              <Input id="label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Near my office" />
            </div>
            <div>
              <Label htmlFor="lat">Latitude</Label>
              <Input id="lat" value={latitude} onChange={(e) => setLatitude(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="lng">Longitude</Label>
              <Input id="lng" value={longitude} onChange={(e) => setLongitude(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="r">Radius (km)</Label>
              <Input id="r" type="number" min="1" value={radius} onChange={(e) => setRadius(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="p">Max price (₹/hr)</Label>
              <Input id="p" type="number" min="1" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} required />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button type="button" variant="outline" onClick={useMyLocation}><MapPin className="h-4 w-4 mr-1" /> Use my location</Button>
              <Button type="submit">Create alert</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <h2 className="text-lg font-semibold mb-3">Your alerts</h2>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : alerts.length === 0 ? (
        <p className="text-muted-foreground">No alerts yet.</p>
      ) : (
        <div className="space-y-3">
          {alerts.map((a) => (
            <Card key={a.id}>
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium">{a.label || `${a.latitude.toFixed(3)}, ${a.longitude.toFixed(3)}`}</p>
                  <p className="text-sm text-muted-foreground">Within {a.radius_km} km · under ₹{a.max_price}/hr</p>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={a.is_active} onCheckedChange={(v) => toggleAlert(a.id, v)} />
                  <Button variant="ghost" size="icon" onClick={() => deleteAlert(a.id)} aria-label="Delete alert">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Alerts;
